import type { FilterConfig, FilterState } from '@/components/filter-bar';
import { TableFilters } from '@/components/filter-bar';
import { FX_SWAP_STATUS_OPTIONS, type FxRecordStatus } from '@/services/api/fx-swaps';
import { toDateInputValue, toLocalEndOfDay, toLocalStartOfDay } from '@/utils/date';

/** Applied filter state — both current filters route to the API after the split. */
export interface FxSwapFilterState {
  /** select-as-array; the split forwards the first element */
  status?: FxRecordStatus[];
  /** local start/end-of-day Dates → startTime/endTime */
  createdAt?: { from?: Date; to?: Date };
}

// The shared FilterEditDialog's `select` type renders raw `options` strings as both the
// dropdown value AND its display label (no separate label support) — so the wire values
// ('SUCCESS'/'FAILED') can't be used directly as `options` without regressing the dialog copy
// from "Success"/"Failed" to the raw enum casing. Map to/from the legacy display
// labels instead, keeping FxSwapFilterState (and everything downstream of it) on the real
// FxRecordStatus wire values.
const STATUS_OPTIONS = FX_SWAP_STATUS_OPTIONS.map((option) => option.label);
const STATUS_LABEL_TO_VALUE = new Map<string, FxRecordStatus>(FX_SWAP_STATUS_OPTIONS.map((option) => [option.label, option.value]));
const STATUS_VALUE_TO_LABEL = new Map<FxRecordStatus, string>(FX_SWAP_STATUS_OPTIONS.map((option) => [option.value, option.label]));

/**
 * Both columns are SERVER-SIDE (status/date route straight to startTime/endTime/status query
 * params — see splitFxSwapFilters in services/api/fx-swaps.ts) — `getValue` is never invoked:
 * TableFilters only calls it through `deriveFilterOptions` for columns lacking a static
 * `options` list, and `status` ships one while `date` never consults it at all.
 */
const FX_SWAP_FILTER_CONFIGS: Array<FilterConfig<unknown>> = [
  { getValue: () => undefined, key: 'status', options: STATUS_OPTIONS, title: 'Status', type: 'select' },
  { getValue: () => undefined, key: 'created_at', title: 'Created At', type: 'date' },
];

interface FxSwapFiltersRowProps {
  filters: FxSwapFilterState;
  onChange: (filters: FxSwapFilterState) => void;
}

/** Filter-chip row + per-filter dialogs, now the shared filter-bar module. */
export const FxSwapFiltersRow = ({ filters, onChange }: FxSwapFiltersRowProps) => {
  const state: FilterState = {
    ...(filters.status?.[0] ? { status: [STATUS_VALUE_TO_LABEL.get(filters.status[0]) ?? filters.status[0]] } : {}),
    ...(filters.createdAt
      ? { created_at: [filters.createdAt.from ? toDateInputValue(filters.createdAt.from) : '', filters.createdAt.to ? toDateInputValue(filters.createdAt.to) : ''] }
      : {}),
  };

  const handleChange = (next: FilterState) => {
    const statusLabel = next.status?.[0];
    const status = statusLabel ? STATUS_LABEL_TO_VALUE.get(statusLabel) : undefined;
    const [dateFrom, dateTo] = next.created_at ?? [];
    onChange({
      status: status ? [status] : undefined,
      createdAt:
        dateFrom || dateTo
          ? {
              ...(dateFrom ? { from: toLocalStartOfDay(dateFrom) } : {}),
              ...(dateTo ? { to: toLocalEndOfDay(dateTo) } : {}),
            }
          : undefined,
    });
  };

  return <TableFilters configs={FX_SWAP_FILTER_CONFIGS} onChange={handleChange} rows={[]} state={state} />;
};
FxSwapFiltersRow.displayName = 'FxSwapFiltersRow';
